/**
 * Syncs the theme product price (and sticky ATC) with the Recharge Widget v2
 * selling_plan selection.
 *
 * Recharge updates the hidden [name="selling_plan"] input programmatically
 * without dispatching change events, so we:
 * 1) intercept the input's value setter
 * 2) listen for change inside <recharge-subscription-widget> shadow root
 * 3) re-apply after theme variant:change (product-rerender resets price)
 *
 * No MutationObserver / continuous polling.
 */
(function () {
  const dataEl = document.getElementById('recharge-price-data');
  if (!dataEl) return;

  let data;
  try {
    data = JSON.parse(dataEl.textContent);
  } catch (e) {
    return;
  }

  if (!data?.formId || !data.variants) return;

  const VALUE_DESCRIPTOR = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
  let watchedInput = null;
  let lastPlanId = null;
  let lastVariantId = null;
  let widgetBound = false;

  function getForm() {
    return (
      document.getElementById(data.formId) ||
      document.querySelector(`form[id="${CSS.escape(data.formId)}"]`)
    );
  }

  function getSellingPlanInput() {
    const form = getForm();
    return (
      (form && form.querySelector('[name="selling_plan"]')) ||
      document.querySelector(`[name="selling_plan"][form="${CSS.escape(data.formId)}"]`) ||
      document.querySelector('[name="selling_plan"]')
    );
  }

  function getSellingPlanId() {
    const input = getSellingPlanInput();
    return input ? String(input.value || '').trim() : '';
  }

  function getVariantId() {
    const form = getForm();
    const idInput =
      (form && form.querySelector('[name="id"]')) ||
      document.querySelector(`[name="id"][form="${CSS.escape(data.formId)}"]`);
    return idInput ? String(idInput.value || '').trim() : null;
  }

  function resolvePrices(variantId, sellingPlanId) {
    const variant = data.variants[variantId];
    if (!variant) return null;

    if (sellingPlanId && variant.plans?.[sellingPlanId]) {
      const plan = variant.plans[sellingPlanId];
      return {
        priceHtml: plan.priceHtml,
        compareHtml: plan.compareHtml || variant.priceHtml,
        onSale: true
      };
    }

    return {
      priceHtml: variant.priceHtml,
      compareHtml: variant.compareHtml,
      onSale: Boolean(variant.compareHtml)
    };
  }

  function setSalePrice(saleEl, priceHtml, onSale, classes) {
    if (!saleEl) return;
    saleEl.className = onSale ? classes.onSale : classes.regular;
    const sr = saleEl.querySelector('.sr-only');
    const srHtml = sr
      ? `<span class="sr-only">${sr.textContent}</span>`
      : data.labels?.sale
        ? `<span class="sr-only">${data.labels.sale}</span>`
        : '';
    saleEl.innerHTML = `${srHtml}${priceHtml}`;
  }

  function setComparePrice(priceList, compareHtml, classes) {
    if (!priceList) return;
    let compareEl = priceList.querySelector('compare-at-price');

    if (!compareHtml) {
      compareEl?.remove();
      return;
    }

    if (!compareEl) {
      compareEl = document.createElement('compare-at-price');
      priceList.appendChild(compareEl);
    }

    compareEl.className = classes.compare;
    const sr = data.labels?.regular
      ? `<span class="sr-only">${data.labels.regular}</span>`
      : '';
    compareEl.innerHTML = `${sr}${compareHtml}`;
  }

  function updatePriceList(root, classes) {
    if (!root) return;
    const priceList = root.querySelector('price-list');
    if (!priceList) return;

    const variantId = getVariantId();
    if (!variantId) return;

    const prices = resolvePrices(variantId, getSellingPlanId());
    if (!prices) return;

    setSalePrice(priceList.querySelector('sale-price'), prices.priceHtml, prices.onSale, classes);
    setComparePrice(priceList, prices.compareHtml, classes);
  }

  function syncPrices() {
    const variantId = getVariantId();
    const planId = getSellingPlanId();

    // Skip no-op updates after the first successful paint for this pair.
    if (variantId === lastVariantId && planId === lastPlanId) return;
    lastVariantId = variantId;
    lastPlanId = planId;

    const productRoot = data.productInfoId
      ? document.getElementById(data.productInfoId)
      : null;
    const stickyRoot = data.stickyId
      ? document.getElementById(data.stickyId)
      : null;

    const productPriceBlock = productRoot?.querySelector('[data-block-type="price"]');
    updatePriceList(productPriceBlock || productRoot, data.classes.product);
    updatePriceList(stickyRoot, data.classes.sticky);
  }

  function forceSync() {
    lastPlanId = null;
    lastVariantId = null;
    syncPrices();
  }

  function watchSellingPlanInput(input) {
    if (!input || input === watchedInput || !VALUE_DESCRIPTOR?.get || !VALUE_DESCRIPTOR?.set) {
      return;
    }

    watchedInput = input;
    let current = VALUE_DESCRIPTOR.get.call(input);

    Object.defineProperty(input, 'value', {
      configurable: true,
      enumerable: true,
      get() {
        return VALUE_DESCRIPTOR.get.call(this);
      },
      set(next) {
        VALUE_DESCRIPTOR.set.call(this, next);
        if (String(next ?? '') !== String(current ?? '')) {
          current = next;
          // Recharge may set value before finishing related DOM updates.
          queueMicrotask(forceSync);
        }
      }
    });
  }

  function bindWidgetShadow() {
    if (widgetBound) return true;
    const widget = document.querySelector('recharge-subscription-widget');
    if (!widget?.shadowRoot) return false;

    widget.shadowRoot.addEventListener('change', () => {
      watchSellingPlanInput(getSellingPlanInput());
      queueMicrotask(forceSync);
      requestAnimationFrame(forceSync);
    });

    widgetBound = true;
    return true;
  }

  function bind() {
    watchSellingPlanInput(getSellingPlanInput());
    bindWidgetShadow();
    forceSync();
  }

  // Capture-phase backup if any path does dispatch native change.
  // Note: form.id is unreliable here because the product form has <input name="id">.
  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!target || target.name !== 'selling_plan') return;

      const targetFormId =
        target.getAttribute('form') || target.form?.getAttribute?.('id');
      if (targetFormId && targetFormId !== data.formId) return;

      forceSync();
    },
    true
  );

  const form = getForm();
  if (form) {
    form.addEventListener('variant:change', () => {
      // Recharge rewrites selling_plan after variant id change; price block is replaced.
      queueMicrotask(() => {
        watchSellingPlanInput(getSellingPlanInput());
        forceSync();
      });
      requestAnimationFrame(() => {
        watchSellingPlanInput(getSellingPlanInput());
        forceSync();
      });
    });
  }

  function start() {
    bind();

    // Widget mounts async; a few short retries, then stop.
    let attempts = 0;
    const maxAttempts = 20;
    const timer = setInterval(() => {
      attempts += 1;
      const input = getSellingPlanInput();
      const widgetReady = bindWidgetShadow();
      if (input) watchSellingPlanInput(input);
      if ((input && widgetReady) || attempts >= maxAttempts) {
        clearInterval(timer);
        forceSync();
      }
    }, 150);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
